import React, { createContext, useContext, useState, useCallback } from 'react';
import { DEFAULT_FORM_DATA, FORM_STEPS } from '../utils/constants';
// Import the refactored simulateLoading and algorithm constants/helpers
import { simulateLoading } from '../utils/logic';
import { ALGORITHM_TYPES, getAlgorithmName } from '../utils/algorithms';

// Create context
const WirelessAdvisorContext = createContext();

/**
 * Custom hook to use the WirelessAdvisor context
 * @returns {Object} Context value
 */
export const useWirelessAdvisor = () => {
  const context = useContext(WirelessAdvisorContext);
  if (!context) {
    throw new Error('useWirelessAdvisor must be used within a WirelessAdvisorProvider');
  }
  return context;
};

/**
 * WirelessAdvisorProvider component
 * @param {Object} props - Component props
 * @param {Object} props.initialState - Initial state for the context
 * @param {React.ReactNode} props.children - Child components
 * @returns {JSX.Element} Provider component
 */
export const WirelessAdvisorProvider = ({ initialState = {}, children }) => {
  // Modal visibility state
  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(initialState.isSelectionModalOpen || false);
  const [isResultsModalOpen, setIsResultsModalOpen] = useState(initialState.isResultsModalOpen || false);

  // Form state
  const [step, setStep] = useState(initialState.step || FORM_STEPS[0]);
  const [formData, setFormData] = useState(initialState.formData || { ...DEFAULT_FORM_DATA });
  // State to hold the consolidated results from all algorithms
  const [allResults, setAllResults] = useState(initialState.allResults || null);
  const [direction, setDirection] = useState(1); // For step transitions (1: forward, -1: backward)

  // State for UI selections within the results modal
  const [selectedAlgorithmType, setSelectedAlgorithmType] = useState(initialState.selectedAlgorithmType || null); // e.g., 'genetic'
  const [selectedCoverageLevel, setSelectedCoverageLevel] = useState(initialState.selectedCoverageLevel || 'medium'); // 'basic', 'medium', 'high'
  const [userNodeCount, setUserNodeCount] = useState(initialState.userNodeCount || null); // User override, null initially

  // State for detailed loading progress
  const [loadingStatus, setLoadingStatus] = useState({ currentAlgorithm: null, progressMessage: null });

  // Get current step index
  const currentStepIndex = FORM_STEPS.indexOf(step);

  /**
   * Update form data
   * @param {Object} newData - New form data to merge with existing data
   */
  const updateFormData = useCallback((newData) => {
    setFormData(prevData => ({
      ...prevData,
      ...newData
    }));
  }, []);

  /**
   * Go to next step
   */
  const nextStep = useCallback(() => {
    if (currentStepIndex < FORM_STEPS.length - 1) {
      setDirection(1);
      setStep(FORM_STEPS[currentStepIndex + 1]);
    }
  }, [currentStepIndex]);

  /**
   * Go to previous step
   */
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setDirection(-1);
      setStep(FORM_STEPS[currentStepIndex - 1]);
    }
  }, [currentStepIndex]);

  /**
   * Go to a specific step
   * @param {string} stepName - Name of the step to go to
   */
  const goToStep = useCallback((stepName) => {
    const stepIndex = FORM_STEPS.indexOf(stepName);
    if (stepIndex !== -1) {
      setDirection(stepIndex > currentStepIndex ? 1 : -1);
      setStep(stepName);
    }
  }, [currentStepIndex]);

  /**
   * Calculate recommendations using all algorithms and show results
   */
  const calculateAndShowResults = useCallback(async () => {
    try {
      // Set direction to forward for the transition
      setDirection(1);

      // Update loading status before starting
      setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Initializing calculations...' });

      // Simulate loading and calculate results for ALL algorithms
      // Pass setLoadingStatus to simulateLoading so it can update progress
      const results = await simulateLoading(
        setStep, // Will set step to 'generating' then 'results'
        formData,
        setAllResults, // Store the consolidated results
        setLoadingStatus // Pass setter for progress updates
      );

      // Clear loading status after completion
      setLoadingStatus({ currentAlgorithm: null, progressMessage: null });

      // Set the default selected algorithm type based on the results
      if (results && results.defaultAlgorithm) {
        setSelectedAlgorithmType(results.defaultAlgorithm);
      } else {
         setSelectedAlgorithmType(ALGORITHM_TYPES.GENETIC); // Fallback default
      }
      // Reset other result-specific selections
      setSelectedCoverageLevel('medium');
      setUserNodeCount(null);

      // Open results modal
      // Consider if selection modal should close here or stay open
      setIsResultsModalOpen(true);

      return results;
    } catch (error) {
      console.error('Error calculating recommendations:', error);
      setLoadingStatus({ currentAlgorithm: null, progressMessage: 'Error during calculation.' }); // Update status on error
      // Handle error appropriately, maybe show an error message
      // Go back to the last form step?
      setStep(FORM_STEPS[FORM_STEPS.length - 1]);
    }
  }, [formData]); // Dependency is only formData now

  /**
   * Reset form data and go back to first step
   */
  const resetForm = useCallback(() => {
    setFormData({ ...DEFAULT_FORM_DATA });
    setAllResults(null); // Reset all results
    setStep(FORM_STEPS[0]);
    setDirection(1);
    setSelectedAlgorithmType(null); // Reset selections
    setSelectedCoverageLevel('medium');
    setUserNodeCount(null);
    setLoadingStatus({ currentAlgorithm: null, progressMessage: null }); // Reset loading status
  }, []);

  // NOTE: recalculateCoverage and customCoverageData are removed.
  // This logic needs to be handled within the ResultsModal or its sub-components.

  /**
   * Update the selected algorithm type in the results view.
   * @param {string} algorithmType - Algorithm type (e.g., 'genetic')
   */
  const updateSelectedAlgorithmType = useCallback((algorithmType) => {
    // Reset user node count when changing algorithm, as recommendations differ
    setUserNodeCount(null);
    setSelectedAlgorithmType(algorithmType);
    // Optionally reset coverage level too? Or keep it? Let's keep it for now.
    // setSelectedCoverageLevel('medium');
  }, []);

  /**
   * Update the selected coverage level ('basic', 'medium', 'high').
   * @param {string} level
   */
  const updateSelectedCoverageLevel = useCallback((level) => {
      // Reset user node count when changing level, to show the recommendation
      setUserNodeCount(null);
      setSelectedCoverageLevel(level);
  }, []);

   /**
   * Update the user-selected node count override.
   * @param {number | null} count - Number of nodes or null to use recommendation
   */
  const updateUserNodeCount = useCallback((count) => {
      setUserNodeCount(count);
  }, []);

  /**
   * Open the selection modal
   */
  const openSelectionModal = useCallback(() => {
    resetForm();
    setIsSelectionModalOpen(true);
  }, [resetForm]);

  /**
   * Close all modals
   */
  const closeModals = useCallback(() => {
    setIsSelectionModalOpen(false);
    setIsResultsModalOpen(false);
  }, []);

  /**
   * Check if a step is complete
   * @param {string} stepName - Name of the step to check
   * @returns {boolean} Whether the step is complete
   */
  const isStepComplete = useCallback((stepName) => {
    switch (stepName) {
      case 'size':
        return !!formData.homeSize;
      case 'routerLocation':
        return !!formData.routerLocation;
      case 'rooms':
        // Ensure rooms object exists and has at least one room with count > 0
        return !!formData.rooms && Object.values(formData.rooms).some(count => count > 0);
      case 'layout':
        return formData.floors >= 1;
      case 'coverage':
        return true; // Optional step
      case 'internetSpeed':
        return !!formData.internetSpeed;
      default:
        return false;
    }
  }, [formData]);

  /**
   * Check if the current step is complete
   * @returns {boolean} Whether the current step is complete
   */
  const isCurrentStepComplete = useCallback(() => {
    return isStepComplete(step);
  }, [step, isStepComplete]);

  // Context value
  const value = {
    // Modal state
    isSelectionModalOpen,
    setIsSelectionModalOpen,
    isResultsModalOpen,
    setIsResultsModalOpen,
    openSelectionModal,
    closeModals,

    // Form state
    step,
    setStep,
    formData,
    updateFormData,
    allResults, // Use allResults instead of recommendation

    // Algorithm, Coverage Level, and Node Count Selection (Results View)
    selectedAlgorithmType,
    updateSelectedAlgorithmType,
    selectedCoverageLevel,
    updateSelectedCoverageLevel,
    userNodeCount,
    updateUserNodeCount,

    // Navigation
    nextStep,
    prevStep,
    goToStep,
    direction,
    currentStepIndex,

    // Validation
    isStepComplete,
    isCurrentStepComplete,

    // Loading Status
    loadingStatus, // Expose loading status

    // Actions
    calculateAndShowResults, // Use this instead of calculateRecommendation
    resetForm
  };

  return (
    <WirelessAdvisorContext.Provider value={value}>
      {children}
    </WirelessAdvisorContext.Provider>
  );
};

export default WirelessAdvisorContext;